import React, { useEffect, useMemo, useState } from 'react';
import $i18n, { withI18n, useI18n, getOSLang } from '@alife/panda-i18n';
import locale from 'src/locale';
import { getConfig } from 'src/services';
import { logCustom, logError, setArmsTrace } from 'src/utils/tracker';
import { RenderReactComponent } from 'src/utils/dom';
import { getQueryConfig, setAppConfig } from 'src/utils/config';
import Entry from './components/entry';
import Panel from './components/panel';
import OsPanel from './components/os-panel';
import Online, { getOnlineUrl } from './components/online';
import Feedback, { goGocPage } from './components/feedback';
import ReleaseNote from './components/release-note';
import HotKnowledgeDetail from './components/hot-knowledge-detail';
import HotKnowledgeSearch from './components/hot-knowledge-search';
import MobileEntry from './components/mobile-entry';
import LightSurvey, { LightSurveyEntry } from './components/light-survey';
export { LightSurvey };
export { AsyncCnIntelliAssistant } from '../utils/async-load';
import QuickEntry, { QuestionnaireTip, isShowQuestionnaire } from './components/quick-entry';
import { PanelType } from 'src/types';
import './index.scss';
import RecordPortal from './components/record-portal';
import { useRecording } from 'src/utils/xreplay';
import { useRender } from 'src/hooks';
import { isDailyAndHint } from 'src/utils';
import { event } from '@ice/stark-data';
import { REFRESH_ALL_KEY } from 'src/utils/const';
$i18n.lockOSLang();
export function setup(props, domId) {
    const operateSet = {
        openPanel() { },
        closePanel() { },
        togglePanel() { },
        getPanel() { return true; },
        openFeedback(param) { },
        openLightSurvey(param) { },
    };
    let instance = null;
    if (props.isMobile) {
        instance = new RenderReactComponent(MobileEntry, { ...props }, 'cn-intelli-assistant-mobile');
    }
    else {
        instance = new RenderReactComponent(CnIntelliAssistant, { ...props, operateSet,
            // env: 'daily',
            // status: undefined,
            // panelCode: 'af5227fb3ae44162bd3f1671918ea9df' 
        }, domId);
    }
    return {
        uninstall() {
            instance.uninstall();
        },
        open() {
            operateSet.openPanel();
        },
        close() {
            operateSet.closePanel();
        },
        toggle() {
            operateSet.togglePanel();
        },
        openFeedback(param) {
            operateSet.openFeedback(param);
        },
        openLightSurvey(param) {
            operateSet.openLightSurvey(param);
        },
        isVisible() {
            return operateSet.getPanel();
        },
    };
}
const CnIntelliAssistant = withI18n(function Assistant(props) {
    const { panelCode, defaultPosition = 'bottom', moziToken, bucToken, userSystem, status, 
    // mode = 'os',
    showEntry = true, direction = 'normal', autoHideEntry = false, showQuestionnaireQuickEntry = true, operateSet } = props;
    const isOs = true;
    const $i18n = useI18n();
    const langCode = $i18n.getLang();
    let moziTicket = useMemo(() => {
        const getTicketMap = {
            mozi: moziToken,
            buc: bucToken,
            cainiao: undefined,
            cbc: undefined,
        };
        return getTicketMap[userSystem];
    }, [moziToken, bucToken, userSystem]);
    // 保存插件参数 方便插件内容复用参数
    useState(() => {
        setAppConfig(props);
        setArmsTrace(props.armsTrace);
    });
    const [visible, setVisible] = useState(false);
    const [config, setConfig] = useState();
    const [panelVisible, setPanelVisible] = useState(false);
    const [releaseNoteVisible, setReleaseNoteVisible] = useState(false);
    const [onlineUrl, setOnlineUrl] = useState('');
    const { isRecording, start, startRecord, stop } = useRecording();
    const init = (callback) => {
        const params = getQueryConfig(props.userSystem);
        getConfig({ ...params, status, language: langCode, }).then((res) => {
            if (!res) {
                console.error(`panelCode ${params.panelCode} configuration not found`);
                logError(new Error('get configuration exception'), {
                    c2: 'get configuration exception'
                });
                return;
            }
            // 注入以下值 方便业务方自定义插槽 调用
            res.userId = params.userId;
            res.userName = params.userName;
            res.accountType = params.userSystem;
            res.panelCode = params.panelCode;
            res.env = params.env;
            setConfig(res);
            callback?.();
        }).catch(error => {
            logError(error, {
                c2: 'get configuration fail'
            });
        });
    };
    useEffect(() => {
        init(() => {
            try {
                props.onInitSuccess?.();
            }
            catch (err) {
                console.error(err);
                logError(err, {
                    c2: 'exec onInitSuccess fail'
                });
            }
            setVisible(true);
            if (operateSet) {
                operateSet.openPanel = () => {
                    setPanelVisible(true);
                };
                operateSet.closePanel = () => {
                    setPanelVisible(false);
                };
                operateSet.openFeedback = (param) => {
                    setPanelVisible(true);
                    setPanelList([[renderFeedback, param]]);
                };
                operateSet.openLightSurvey = (param) => {
                    setTempLightSurveyData(param);
                };
            }
        });
        const fn = () => {
            init();
        };
        event.on(REFRESH_ALL_KEY, fn);
        return () => {
            event.off(REFRESH_ALL_KEY, fn);
        };
    }, [props.userSystem, props.panelCode]);
    // 缓存知识点的多tab的值
    const [currentHotTab, setCurrentHotTab] = useState(0);
    useEffect(() => {
        if (!panelVisible) {
            setCurrentHotTab(0);
        }
    }, [panelVisible]);
    useEffect(() => {
        if (operateSet) {
            operateSet.togglePanel = () => {
                setPanelVisible(!panelVisible);
            };
            operateSet.getPanel = () => panelVisible;
        }
    }, [panelVisible]);
    const hasOnlineService = config?.xlPanelConfigDTOList?.some?.((it) => it.panelConfigCode === PanelType.onlineService && it.data?.accessUrl);
    const openOnlineService = (param) => {
        const onlineServiceConfig = config?.xlPanelConfigDTOList?.filter?.((it) => it.panelConfigCode === PanelType.onlineService && it.data?.accessUrl)[0];
        if (onlineServiceConfig) {
            onTypeClick(PanelType.onlineService, onlineServiceConfig.data, param);
        }
    };
    const onTypeClick = async (type, config, onlineUrlParam) => {
        if (type === PanelType.questionnaire) {
            window.open(config);
            return;
        }
        if (type === PanelType.releaseNote) {
            setReleaseNoteVisible(true);
            return;
        }
        if (type === PanelType.onlineService) {
            if (isDailyAndHint('在线服务')) {
                return;
            }
            const url = await getOnlineUrl({ moziToken: moziTicket, config, lang: langCode, urlParam: onlineUrlParam });
            if (!url) {
                return;
            }
            // console.log(config);
            if (config?.accessStyle === 'pc') {
                window.open(url);
                return;
            }
            setOnlineUrl(url);
            pushPanel(renderOnlineService, { onlineUrl: url });
        }
        if (type === PanelType.hotQuestion) {
            pushPanel(renderHotKnowledgeDetail, { id: config });
        }
        if (type === PanelType.hotQuestionSearch) {
            if (!config) {
                pushPanel(renderHotKnowledgeSearch);
            }
            else {
                replacePanel(renderHotKnowledgeSearch, config);
            }
        }
        if (type === PanelType.feedback) {
            if (isDailyAndHint('意见反馈')) {
                return;
            }
            if (config?.caseType === 'gocCase') {
                goGocPage(config.gocCaseUrl);
                logCustom('CLK_go_goc_page', config.gocCaseUrl, panelCode);
                return;
            }
            pushPanel(renderFeedback);
        }
        // setPanelVisible(!panelVisible);
    };
    const onClose = () => {
        setPanelVisible(false);
    };
    const showOtherContent = useMemo(() => {
        return isShowQuestionnaire(config);
    }, [config]);
    const [panelList, setPanelList] = useState([]);
    const onBackPrevPanel = () => {
        setPanelList((list) => [...list.slice(0, -1)]);
    };
    const [tempLightSurveyData, setTempLightSurveyData] = useState(null);
    useEffect(() => {
        if (!panelVisible) {
            setPanelList([]);
        }
    }, [panelVisible]);
    const pushPanel = (...args) => setPanelList((list) => [...list, args]);
    const replacePanel = (...args) => setPanelList((list) => [...list.slice(0, -1), args]);
    const renderPanel = useRender(() => React.createElement(Panel, { config: config, onTypeClick: onTypeClick, panelCode: panelCode, ...{
            userSystem,
            env: props.env,
            currentHotTab, setCurrentHotTab
        } }));
    // console.log('pa', panelList);
    const renderOnlineService = useRender((params) => React.createElement(Online, { onlineUrl: onlineUrl, ...params, panelCode: panelCode, onBack: onBackPrevPanel }));
    const renderFeedback = useRender((params) => React.createElement(Feedback, { ...props, ...params, config: config, onClose: onClose, startRecord: startRecord, onBack: onBackPrevPanel }));
    const renderHotKnowledgeDetail = useRender((params) => React.createElement(HotKnowledgeDetail, { ...props, config: config, ...params, onClose: onClose, hasOnlineService: hasOnlineService, openOnlineService: openOnlineService, onBack: onBackPrevPanel }));
    const renderHotKnowledgeSearch = useRender((params) => React.createElement(HotKnowledgeSearch, { ...props, ...params, onItemClick: onTypeClick, onBack: onBackPrevPanel }));
    useEffect(() => {
        if (visible) {
            logCustom('EXP_helpCenterPlugin', '', panelCode);
        }
    }, [visible]);
    if (!visible)
        return null;
    const lightSurveyConfig = config?.xlPanelConfigDTOList?.filter?.((it) => it.panelConfigCode === PanelType.lightSurvey)[0];
    const renderInfo = () => {
        const [render, param] = panelList.slice(-1)[0] || [];
        if (!panelVisible)
            return null;
        return render ? render(param) : renderPanel();
    };
    const showLightSurveyList = tempLightSurveyData || lightSurveyConfig?.data?.lightSurveyList;
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: isRecording ? 'cn-intelli-assistant-recording' : '' },
            React.createElement(Entry, { isOs: isOs, showEntry: showEntry, direction: direction, autoHideEntry: autoHideEntry, onClick: () => {
                    setPanelVisible(!panelVisible);
                }, position: defaultPosition, panelCode: panelCode, panelVisible: panelVisible, otherEntryNode: showOtherContent ? React.createElement("div", { className: 'cn-intelli-assistant-other-entry' },
                    showQuestionnaireQuickEntry ? React.createElement(QuickEntry, { config: config, onTypeClick: onTypeClick }) : null,
                    showEntry ? React.createElement(QuestionnaireTip, { config: config, onTypeClick: onTypeClick, userId: props.userId, userSystem: props.userSystem }) : null) : null }),
            React.createElement(OsPanel, { showClose: true, visible: panelVisible, clickOutsideClose: false, onClose: () => {
                    props.onClose?.();
                    setPanelVisible(false);
                } }, renderInfo()),
            React.createElement(ReleaseNote, { panelCode: panelCode, visible: releaseNoteVisible, onClose: () => setReleaseNoteVisible(false) })),
        showLightSurveyList
            ? React.createElement(LightSurveyEntry, { list: lightSurveyConfig?.data?.lightSurveyList, tempLightSurveyData: tempLightSurveyData, closeLightSurveyData: () => {
                    setTempLightSurveyData(null);
                }, config: config })
            : null,
        isRecording ? React.createElement(RecordPortal, { onStart: start, onStop: stop }) : null));
}, {
    locale,
    lang: getOSLang(),
});
export function preview(props) {
    return setup(props, 'cn-intelli-assistant-preview');
}
export const openFeedbackApi = true;
export default function Comp(props) {
    useEffect(() => {
        const instance = setup(props);
        // @ts-ignore
        if (window.CNIntelliAssistantInstance) {
            // @ts-ignore
            window.CNIntelliAssistantInstance.uninstall?.();
        }
        // @ts-ignore
        window.CNIntelliAssistantInstance = instance;
        return () => {
            instance.uninstall();
        };
    }, [props?.panelCode]);
    return null;
}
export function CnIntelliAssistantMEntry(props) {
    useEffect(() => {
        const instance = new RenderReactComponent(MobileEntry, { ...props,
        }, 'cn-intelli-assistant-mobile');
        return () => {
            instance.uninstall();
        };
    }, [props?.panelCode]);
    return React.createElement(React.Fragment, null);
}
